import { useEffect, useRef, useState } from 'react';
import { Editor, Renderer2D, createFromSpec, boundsOfNodes, TEMPLATES, type Template } from '@vellum/core';
import { app } from '../app.ts';
import { useApp } from '../hooks.ts';
import { command } from '../commands.ts';

/** Insert a template at the center of the view and select it. */
export function insertTemplateById(t: Template) {
  const c = app.screenToPage({ x: app.viewportSize.width / 2, y: app.viewportSize.height / 2 });
  // build once off-screen to learn its size, then place it centred
  const probe = new Editor();
  const made = createFromSpec(probe, t.build(0, 0));
  const b = boundsOfNodes(probe.doc, made.ids) ?? { x: 0, y: 0, width: 0, height: 0 };
  let x = Math.round(c.x - b.width / 2),
    y = Math.round(c.y - b.height / 2);
  // don't land on top of existing work: go to the right of it instead
  const page = app.doc.children(app.editor.pageId).map((n) => n.id);
  const content = page.length ? boundsOfNodes(app.doc, page) : null;
  const hits = page.some((id) => {
    const r = app.doc.worldBounds(id);
    return r.x < x + b.width && r.x + r.width > x && r.y < y + b.height && r.y + r.height > y;
  });
  if (hits && content) {
    x = Math.round(content.x + content.width + 200);
    y = Math.round(content.y);
  }
  const res = createFromSpec(app.editor, t.build(x - b.x, y - b.y));
  app.editor.setSelection(res.ids);
  app.setUI({ templatesOpen: false });
  if (hits) command('zoomSelection').run();
}

/** A live preview: the template drawn by the same renderer exports use. */
function Preview({ t }: { t: Template }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const c = ref.current!;
    const dpr = window.devicePixelRatio || 1;
    c.width = 220 * dpr;
    c.height = 140 * dpr;
    const ed = new Editor();
    const made = createFromSpec(ed, t.build(0, 0));
    const b = boundsOfNodes(ed.doc, made.ids);
    if (!b) return;
    const pad = 10;
    const s = Math.min((220 - pad * 2) / b.width, (140 - pad * 2) / b.height) * dpr;
    const ctx = c.getContext('2d')!;
    const tx = (c.width - b.width * s) / 2 - b.x * s,
      ty = (c.height - b.height * s) / 2 - b.y * s;
    new Renderer2D(ed.doc).renderPage(ctx, ed.pageId, { transform: [s, 0, 0, s, tx, ty], background: false });
  }, [t]);
  return <canvas ref={ref} style={{ width: 220, height: 140 }} />;
}

export function TemplatesDialog() {
  const open = useApp('ui', () => app.ui.templatesOpen);
  const kind = useApp('doc', () => app.pageKind());
  const [filter, setFilter] = useState<'WHITEBOARD' | 'DESIGN'>(kind === 'DESIGN' ? 'DESIGN' : 'WHITEBOARD');
  useEffect(() => setFilter(kind === 'DESIGN' ? 'DESIGN' : 'WHITEBOARD'), [kind, open]);
  if (!open) return null;
  const list = TEMPLATES.filter((t) => t.kind === filter);
  return (
    <div className="modal-backdrop" onPointerDown={(e) => e.target === e.currentTarget && app.setUI({ templatesOpen: false })}>
      <div className="modal" style={{ width: 'min(780px, calc(100vw - 32px))' }}>
        <header>
          <span>Templates</span>
          <span style={{ display: 'flex', gap: 6 }}>
            <button className={`btn ${filter === 'WHITEBOARD' ? 'primary' : 'secondary'}`} onClick={() => setFilter('WHITEBOARD')}>
              Whiteboard
            </button>
            <button className={`btn ${filter === 'DESIGN' ? 'primary' : 'secondary'}`} onClick={() => setFilter('DESIGN')}>
              Design
            </button>
            <button className="icon-btn" onClick={() => app.setUI({ templatesOpen: false })}>
              ✕
            </button>
          </span>
        </header>
        <div className="body template-grid">
          {list.map((t) => (
            <button key={t.id} className="template-card" onClick={() => insertTemplateById(t)} disabled={app.readOnly}>
              <Preview t={t} />
              <b>{t.name}</b>
              <span>{t.description}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
